import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert } from "@mui/material";
import { studentFieldDefinitions } from "./student-field-definition";
import { User } from "../../model/user";
import { useToast } from "../../../../context/toast/toast-provider";
import { ACCOUNT_MANAGEMENT_STUDENT_EDIT, ACCOUNT_MANAGEMENT_STUDENT_LIST, PARAMETER_STUDENT_ID } from "../../../../infrastructure/route";
import BaseForm from "../../../../component/form/base-form";
import { addUserStudent, deleteUserStudent, updateUserStudent } from "../../service/student-service";
import { Cohort } from "../../../-education/model/cohort";
import { getCohorts } from "../../../-education/service/cohort-service";
import React from "react";
import ViewLoader from "../../../../component/misc/view-loader";
import { SelectItem } from "../../../../component/form/select-item";
import { studentRoles, Role } from "../../../enum/role";
import { Gender } from "../../../enum/gender";
import { CourseLevel } from "../../../-education/enum/course-level";
import { Stream, StudentGroup } from "../../../-education/model/stream";
import { getStreams } from "../../../-education/service/stream-service";
import { getStudentGroups, getStudentGroupByStudentId, addGroupMember, removeGroupMember } from "../../../-education/service/student-group-service";

const getRoleCourseLevels = (studentRole: number | undefined): number[] => {
    switch(studentRole) {
        case Role.Explorer:
            return [CourseLevel.Mobile_Leap];
        case Role.BootcampTrainee:
            return [CourseLevel.BootCamp];
        case Role.Student:
            return [CourseLevel.Advanced];
        case Role.LoropioGirlsExplorer:
            return [CourseLevel.LG_High_School];
        default:
            return [];
    }
}

export default function StudentHandler({
    newModel,
    student,
    setStudent
}: {
        newModel: boolean,
        student: User,
        setStudent: React.Dispatch<React.SetStateAction<User>>
}) {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [cohorts, setCohorts] = useState<Cohort[]>([]);
    const [streams, setStreams] = useState<Stream[]>([]);
    const [groups, setGroups] = useState<StudentGroup[]>([]);
    const [currentGroupId, setCurrentGroupId] = useState<number | undefined>(undefined);

    const toast = useToast();
    const navigate = useNavigate();

    const isLoropioGirl = student.studentRole === Role.LoropioGirlsExplorer;

    useEffect(() => {
        setLoading(true);
        getCohorts().then(result => {
            setCohorts(result);
            setLoading(false);
        }).catch(() => {})
    }, []);

    // Load streams when cohort changes (only for LoropioGirlsExplorer)
    const studentCohortId = student.cohortId;
    useEffect(() => {
        if (isLoropioGirl && studentCohortId && studentCohortId !== 0) {
            getStreams(studentCohortId).then(result => {
                setStreams(result);
            }).catch(() => setStreams([]));
        } else {
            setStreams([]);
            setGroups([]);
        }
    }, [studentCohortId, isLoropioGirl]);

    // Load groups when stream changes
    const studentStreamId = (student as any).streamId;
    useEffect(() => {
        if (isLoropioGirl && studentStreamId && studentStreamId !== 0) {
            getStudentGroups(studentStreamId).then(result => {
                setGroups(result);
            }).catch(() => setGroups([]));
        } else {
            setGroups([]);
        }
    }, [studentStreamId, isLoropioGirl]);

    // Load current group membership when editing an existing LoropioGirlsExplorer student
    const studentId = student.id;
    useEffect(() => {
        if (!newModel && isLoropioGirl && studentId) {
            getStudentGroupByStudentId(studentId).then(result => {
                setCurrentGroupId(result.id);
                // Set streamId and groupId on the model so the selects show current values
                if (result.streamId) {
                    setStudent(prev => ({ ...prev, streamId: result.streamId, groupId: result.id } as any));
                }
            }).catch(() => { /* Student not in any group */ });
        }
    }, [newModel, isLoropioGirl, studentId, setStudent]);

    const getStudentFieldDefinitions = () => {
        let cohortsItems = [{
            id: 0,
            name: "None"
        } as SelectItem]
        
        if (cohorts && cohorts.length > 0) {
            // Filter cohorts based on student's role
            const allowedCourseLevels = getRoleCourseLevels(student.studentRole);
            const filteredCohorts = allowedCourseLevels.length > 0 
                ? cohorts.filter(_ => allowedCourseLevels.includes(_.courseLevelId!))
                : cohorts;
            
            cohortsItems = cohortsItems.concat(filteredCohorts.map(_ => {
                return {
                    id: _.id,
                    name: _.name
                } as SelectItem
            }))
        }
        
        studentFieldDefinitions.find(_ => _.id === "cohortId")!.values = cohortsItems;

        // Stream and group fields for LoropioGirlsExplorer
        const streamField = studentFieldDefinitions.find(_ => _.id === "streamId");
        const groupField = studentFieldDefinitions.find(_ => _.id === "groupId");

        if (streamField) {
            streamField.hidden = !isLoropioGirl;
            streamField.disabled = !student.cohortId || student.cohortId === 0;
            let streamItems = [{ id: 0, name: "None" } as SelectItem];
            if (streams && streams.length > 0) {
                streamItems = streamItems.concat(streams.map(_ => ({ id: _.id, name: _.name } as SelectItem)));
            }
            streamField.values = streamItems;
        }

        if (groupField) {
            groupField.hidden = !isLoropioGirl;
            groupField.disabled = !(student as any).streamId || (student as any).streamId === 0;
            let groupItems = [{ id: 0, name: "None" } as SelectItem];
            if (groups && groups.length > 0) {
                groupItems = groupItems.concat(groups.map(_ => ({ id: _.id, name: _.name } as SelectItem)));
            }
            groupField.values = groupItems;
        }

        if(student.hasNonStudentRole === true) {
            studentFieldDefinitions.find(_ => _.id === "name")!.disabled = true;
            studentFieldDefinitions.find(_ => _.id === "phone")!.disabled = true;
            studentFieldDefinitions.find(_ => _.id === "email")!.disabled = true;
            studentFieldDefinitions.find(_ => _.id === "gender")!.disabled = true;
            studentFieldDefinitions.find(_ => _.id === "gender")!.required = false;
            studentFieldDefinitions.find(_ => _.id === "cohortId")!.disabled = false;
            studentFieldDefinitions.find(_ => _.id === "active")!.disabled = true;
        } else {
            studentFieldDefinitions.find(_ => _.id === "name")!.disabled = false;
            studentFieldDefinitions.find(_ => _.id === "phone")!.disabled = false;
            studentFieldDefinitions.find(_ => _.id === "email")!.disabled = false;
            studentFieldDefinitions.find(_ => _.id === "gender")!.disabled = false;
            studentFieldDefinitions.find(_ => _.id === "gender")!.required = true;
            studentFieldDefinitions.find(_ => _.id === "cohortId")!.disabled = student.gender === undefined;
            studentFieldDefinitions.find(_ => _.id === "active")!.disabled = false;
        }

        return studentFieldDefinitions;
    }

    const handleGroupAssignment = async (studentId: number, newGroupId: number | undefined) => {
        // Remove from old group if exists
        if (currentGroupId && currentGroupId !== newGroupId) {
            try {
                await removeGroupMember(currentGroupId, studentId);
            } catch { /* Student might not be in old group */ }
        }

        // Add to new group if selected
        if (newGroupId && newGroupId !== 0 && newGroupId !== currentGroupId) {
            try {
                await addGroupMember(newGroupId, { groupId: newGroupId, studentId: studentId });
                setCurrentGroupId(newGroupId);
                toast.addToast(`Student added to group`, "success");
            } catch {
                toast.addToast(`Unable to add student to group`, "error");
            }
        }
    }

    const handleSubmit = (studentUser: User) => {
        setSubmitting(true);

        var roles = studentUser.roles.filter(r => studentRoles.find(sr => r === sr) === undefined);
        studentUser.roles = roles;
        studentUser.roles.push(studentUser.studentRole!);
        studentUser.cohortId = studentUser.cohortId === 0 ? undefined : studentUser.cohortId;

        const selectedGroupId = (studentUser as any).groupId;
        const groupId = selectedGroupId && selectedGroupId !== 0 ? selectedGroupId : undefined;

        if (newModel) {
            addUserStudent(studentUser).then(result => {
                toast.addToast(`Student ${result.name} created`, "success");
                result.studentRole = studentRoles.find(sr => result.roles.find(r => r === sr));
                result.cohortId = result.cohortId === undefined ? 0 : result.cohortId;
                setStudent(result);
                setSubmitting(false);

                if (isLoropioGirl && groupId) {
                    handleGroupAssignment(result.id, groupId);
                }

                navigate(ACCOUNT_MANAGEMENT_STUDENT_EDIT.replace(PARAMETER_STUDENT_ID, `${result.id}`))
            }).catch((error) => {
                if (error?.apiErrorResponse?.errorCode === 409) {
                    toast.addToast(`The email ${studentUser.email} already exists`, "error");
                } else {
                    toast.addToast(`Unable to create student`, "error");
                }
                setSubmitting(false);
            });
        } else {
            updateUserStudent(studentUser).then(result => {
                toast.addToast(`Student ${result.name} updated`, "success");
                result.studentRole = studentRoles.find(sr => result.roles.find(r => r === sr));
                result.cohortId = result.cohortId === undefined ? 0 : result.cohortId;
                setStudent(result);
                setSubmitting(false);

                if (isLoropioGirl) {
                    handleGroupAssignment(result.id, groupId);
                }
            }).catch((error) => {
                if (error?.apiErrorResponse?.errorCode === 409) {
                    toast.addToast(`The email ${studentUser.email} already exists`, "error");
                } else {
                    toast.addToast(`Unable to update student ${studentUser.name}`, "error");
                }
                setSubmitting(false);
            });
        }
    }

    const deleteWarnings = () => {
        const warnings = [
            student.hasAttendances === true ? `<b>${student.name}</b> has <b>attendance</b>, deleting the student will also <b>delete the attendance</b>.` : '',
            student.hasAssignments === true ? `<b>${student.name}</b> has <b>assignments</b>, deleting the student will also <b>delete the assignments</b>.` : '',
            student.hasPresentations === true ? `<b>${student.name}</b> has given <b>presentations</b>, deleting the student will also <b>delete the presentations</b>.` : '',
            student.hasSurveySubmits === true ? `<b>${student.name}</b> submitted <b>surveys</b>, deleting the user will also <b>delete the survey submits</b>.` : '',
            student.cohortId !== undefined ? `<b>${student.name}</b> as a student is attending a <b>cohort ${student.cohortName}</b>, deleting the student will also <b>remove it from the cohort</b>.` : '',
        ].filter(_ => _.length > 0)

        return warnings.length > 0 ? warnings : undefined;
    }

    const handleDelete = () => {
        if (!newModel) {
            setDeleting(true);
            deleteUserStudent(Number(student.id)).then(() => {
                toast.addToast(`Student ${student.name} deleted`, "success");
                setDeleting(false);
                navigate(ACCOUNT_MANAGEMENT_STUDENT_LIST);
            }).catch(() => {
                toast.addToast(`Unable to delete student ${student.name}`, "error");
                setDeleting(false);
            });
        }
    }

    const cohortGenderWarning = () => {
        if (!cohorts || cohorts.length === 0) return false;
        
        const selectedCohortGender = cohorts.find(_ => _.id === student.cohortId)?.gender;
        return selectedCohortGender !== undefined && 
            selectedCohortGender !== Gender.All &&
            student.gender !== undefined &&
            selectedCohortGender !== student.gender;
    }

    const handleModelChange = (updatedModel: any) => {
        // Reset stream and group when cohort changes
        if (updatedModel.cohortId !== student.cohortId) {
            updatedModel.streamId = 0;
            updatedModel.groupId = 0;
        }
        // Reset group when stream changes
        if (updatedModel.streamId !== (student as any).streamId) {
            updatedModel.groupId = 0;
        }
        // Reset stream/group when role changes away from LoropioGirlsExplorer
        if (updatedModel.studentRole !== Role.LoropioGirlsExplorer) {
            updatedModel.streamId = 0;
            updatedModel.groupId = 0;
        }
        setStudent(updatedModel);
    }

    return (
        <React.Fragment>
            <ViewLoader loading={loading} />
            {loading === false && <BaseForm
                name={student.name}
                model={student}
                setModel={handleModelChange}
                fields={getStudentFieldDefinitions()}
                submitEntity={handleSubmit}
                deleteEntity={!newModel && !student.hasNonStudentRole ? handleDelete : undefined}
                submitting={submitting}
                deleting={deleting}
                deleteWarnings={deleteWarnings()}
            >
                <React.Fragment>
                    {cohortGenderWarning() === true && <Alert severity="warning">
                        <span dangerouslySetInnerHTML={{ __html: `Cohort gender is <b>${Gender[cohorts.find(_ => _.id === student.cohortId)!.gender!]}</b> but student gender is <b>${Gender[student.gender!]}</b>.` }}></span>
                    </Alert>}
                    <span style={{ display: student.hasNonStudentRole === true ? 'inline': 'none'}}>
                        {student.name} has more responsibility then beening a student. You can only update <b>Cohort</b> and <b>Student type</b>.
                    </span>
                </React.Fragment>
            </BaseForm>}
        </React.Fragment>
    );
}
